import type { Operator } from '../schemas/filter-entity'
import {
  OPERATOR_LABELS,
  isRangeValue,
  isRollingDateValue,
  getRollingDateLabel,
} from '../schemas/filter-entity'
import { formatCompactNumber } from '@/components/grid/formatters'
import type { ColumnCatalogDef } from '@/types/column-catalog'

// ─── Provenance (structural) ───
//
// The summary only reads `source` (chip styling) and `label` (chip tooltip);
// feature-level provenance maps (e.g. history's `LeafProvenance`, whose
// `source` is a narrower union) are structurally assignable.

/** Provenance metadata for a leaf added by an interactive trigger. */
export interface FilterLeafMeta {
  source: string
  label: string
}

/** Map from `leafSignature(column, operator, value)` → its provenance. */
export type FilterLeafProvenance = Record<string, FilterLeafMeta>

// ─── Types ───

export interface FilterChip {
  column: string
  label: string
  display: string
  isDate: boolean
  /** When true, this chip represents multiple OR'd conditions joined by " or ". */
  isOrGroup?: boolean
  /** Provenance of the leaf this chip represents (drill/ask/similar/sankey),
   *  when it was added through an interactive trigger. Drives the chip label
   *  and styling. */
  provenance?: FilterLeafMeta
  /** Derived alias: true when `provenance.source === 'sankey'`. Kept for the
   *  header's existing Sankey chip styling. */
  fromSankey?: boolean
}

/**
 * Stable identity for a single filter leaf. Used to tag chips with their
 * provenance (e.g. Sankey drills). Must be computed identically wherever it is
 * produced and compared — see history-sankey-panel's drill handler.
 */
export function leafSignature(column: string, operator: string, value: unknown): string {
  return `${column}|${operator}|${JSON.stringify(value ?? null)}`
}

/** Signature of a query-format leaf node (`{ left, operator, right }`), or null
 *  when the node isn't a recognizable leaf (e.g. a nested AND/OR group). */
function queryLeafSignature(node: unknown): string | null {
  if (!node || typeof node !== 'object') return null
  const n = node as {
    left?: { column?: string }
    operator?: string
    right?: { value?: unknown; column?: string }
  }
  if (n.left?.column == null || n.operator == null || n.right == null) return null
  const value = 'value' in n.right ? n.right.value : n.right.column
  return leafSignature(n.left.column, n.operator, value)
}

/**
 * Removes the top-level AND leaves whose signature is in `signatures` — the
 * inverse of the Sankey drill's `mergeHistoryFilters` (which appends its
 * predicate leaves to the top-level AND). Used to discard a persisted drill on
 * page load so the drill behaves as an ephemeral, session-scoped selection.
 *
 * Only the top level is touched (that's the sole place drills add to), which
 * also keeps nested groups untouched. A manual filter identical to a drill leaf
 * would share its signature and be removed too — the same collision the
 * provenance tagging already accepts; Sankey stage columns are rarely filtered
 * by hand.
 */
export function stripLeavesBySignature(filters: unknown, signatures: ReadonlySet<string>): unknown {
  if (signatures.size === 0) return filters
  if (!filters || typeof filters !== 'object' || Array.isArray(filters)) return filters
  const rec = filters as Record<string, unknown>
  if (!Array.isArray(rec.AND)) return filters
  const kept = (rec.AND as unknown[]).filter((node) => {
    const sig = queryLeafSignature(node)
    return sig == null || !signatures.has(sig)
  })
  return { ...rec, AND: kept }
}

export interface FilterSummary {
  dateRange: string | null
  chips: FilterChip[]
  totalCount: number
}

// ─── Normalized leaf ───

/** Flattened representation that works regardless of input format. */
interface NormalizedLeaf {
  column: string
  operator: string
  value: unknown
  /** The value as stored, before the between `[low, high]` → `{ low, high }`
   *  normalization. Provenance keys are signed from this shape. */
  rawValue: unknown
  isColumnCompare: boolean
}

// ─── Helpers ───

interface RawLeaf {
  // Form format fields
  column?: string
  operator?: string
  value?: unknown
  rightType?: string
  enabled?: boolean
  // QueryFilterNode format fields
  left?: { column?: string }
  right?: { value?: unknown; column?: string }
  allowNull?: boolean
}

function isGroup(node: unknown): node is Record<string, unknown[]> {
  if (!node || typeof node !== 'object') return false
  return 'AND' in node || 'OR' in node
}

/** Normalize a raw leaf from either format into a common shape. */
function normalizeLeaf(raw: RawLeaf): NormalizedLeaf | null {
  const column = raw.column ?? raw.left?.column ?? ''
  if (!column) return null

  const operator = raw.operator ?? ''

  // Determine if this is a column-vs-column comparison
  const isColumnCompare =
    raw.rightType === 'column' ||
    (raw.right != null && 'column' in raw.right && !('value' in raw.right))

  // Extract the actual value from whichever format
  let value: unknown
  if (raw.value !== undefined) {
    // Form format: value is top-level
    value = raw.value
  } else if (raw.right != null) {
    if ('value' in raw.right) {
      value = raw.right.value
    } else if ('column' in raw.right) {
      value = raw.right.column
    }
  }

  const rawValue = value

  // QueryFilterNode between stores range as [low, high] array — normalize
  if (
    operator === 'between' &&
    Array.isArray(value) &&
    value.length === 2 &&
    (typeof value[0] === 'number' || typeof value[0] === 'string')
  ) {
    value = { low: value[0], high: value[1] }
  }

  return { column, operator, value, rawValue, isColumnCompare }
}

function isEnabled(raw: RawLeaf): boolean {
  // Form format has explicit enabled; QueryFilterNode has no such field (always enabled)
  return raw.enabled !== false
}

function formatValue(value: unknown, operator: string, dataType: string): string {
  const prefix = dataType === 'currency' ? '$' : ''
  const suffix = dataType === 'percent' ? '%' : ''

  if (operator === 'between' && isRangeValue(value)) {
    const lo = formatSingle(value.low, dataType)
    const hi = formatSingle(value.high, dataType)
    return `${prefix}${lo}–${hi}${suffix}`
  }

  if (operator === 'in' || operator === 'not_in') {
    if (Array.isArray(value)) return value.join(', ')
    return String(value ?? '')
  }

  if (operator === 'is_true') return 'True'
  if (operator === 'is_false') return 'False'

  const formatted = formatSingle(value as number, dataType)
  return `${prefix}${formatted}${suffix}`
}

function formatSingle(value: unknown, dataType: string): string {
  const num = Number(value)
  if (isNaN(num)) return String(value ?? '')
  if (dataType === 'volume' || Math.abs(num) >= 100_000) {
    return formatCompactNumber(num)
  }
  return num % 1 === 0 ? String(num) : num.toFixed(2).replace(/\.?0+$/, '')
}

function formatDateRange(leaves: NormalizedLeaf[]): string | null {
  if (leaves.length === 0) return null

  const fmt = (raw: unknown): string => {
    // Check for rolling date keys first
    if (isRollingDateValue(raw)) {
      return getRollingDateLabel(raw as string) ?? String(raw)
    }
    const s = String(raw)
    try {
      const d = new Date(s + 'T00:00:00')
      if (isNaN(d.getTime())) return s
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
      return s
    }
  }

  // If there are two leaves (gte + lte), combine into a range
  if (leaves.length >= 2) {
    const gteLeaf = leaves.find((l) => l.operator === 'gte' || l.operator === 'gt')
    const lteLeaf = leaves.find((l) => l.operator === 'lte' || l.operator === 'lt')
    if (gteLeaf && lteLeaf) {
      return `${fmt(gteLeaf.value)} – ${fmt(lteLeaf.value)}`
    }
  }

  for (const leaf of leaves) {
    const { operator: op, value: val } = leaf

    if (op === 'between' && isRangeValue(val)) {
      // If the low is a rolling key, just show its label
      if (isRollingDateValue(val.low)) {
        return getRollingDateLabel(val.low as string) ?? `${fmt(val.low)} – ${fmt(val.high)}`
      }
      return `${fmt(val.low)} – ${fmt(val.high)}`
    }
    // Rolling date value with gte → show label only
    if ((op === 'gte' || op === 'gt') && isRollingDateValue(val)) {
      return getRollingDateLabel(val as string) ?? `${fmt(val)} – now`
    }
    if (op === 'gte' || op === 'gt') return `${fmt(val)} – now`
    if (op === 'lte' || op === 'lt') return `up to ${fmt(val)}`
    if (op === 'eq') return fmt(val)
  }

  return null
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/

function isDateLike(value: unknown): value is string {
  return typeof value === 'string' && (isRollingDateValue(value) || ISO_DATE.test(value))
}

function rollingSpan(key: string): string {
  const label = getRollingDateLabel(key) ?? key.replace(/^rolling_/, '').replace(/_/g, ' ')
  return `Rolling ${label.toLowerCase()}`
}

/** Other date columns (IPO Date, …) carry no date dataType, so they are read by their values. */
function formatDateValue(op: string, value: unknown): string | null {
  const one = (v: string) => (ISO_DATE.test(v) ? v.slice(0, 10) : rollingSpan(v))
  if (op === 'between' && isRangeValue(value) && isDateLike(value.low) && isDateLike(value.high)) {
    if (isRollingDateValue(value.low)) return rollingSpan(value.low)
    return `${one(value.low)} – ${one(value.high)}`
  }
  if ((op === 'gte' || op === 'gt') && isRollingDateValue(value)) {
    return rollingSpan(value)
  }
  return null
}

function operatorSymbol(op: string): string {
  return OPERATOR_LABELS[op as Operator] ?? op
}

// ─── Main ───

/**
 * Only the date window a filter tree selects, as `extractFilterSummary` would
 * word it ("Jun 1, 2026 – Sep 5, 2026", "90 Days" for a rolling low), or null
 * when the tree has no `day0_date` leaf. For surfaces that want the window
 * without the chips and need no column catalog: the Backtester's Setup step
 * names a universe by its conditions and its dates.
 */
export function extractFilterDateRange(filters: unknown): string | null {
  const rawLeaves: RawLeaf[] = []
  collectLeaves(filters, rawLeaves)
  const dateLeaves = rawLeaves
    .filter(isEnabled)
    .map(normalizeLeaf)
    .filter((l): l is NormalizedLeaf => l !== null && l.column === 'day0_date')
  return formatDateRange(dateLeaves)
}

/**
 * Walks a filter tree and extracts a flat list of chip descriptors plus an
 * optional date-range string.
 *
 * Handles both formats:
 * - Form format:  `[{ AND: [{ column, operator, value, rightType, enabled }] }]`
 * - Query format: `{ AND: [{ left: { column }, operator, right: { value } }] }`
 */
export function extractFilterSummary(
  filters: unknown,
  columnMap: Record<string, ColumnCatalogDef>,
  provenance?: FilterLeafProvenance,
): FilterSummary {
  // Flat collection for total count + date extraction
  const rawLeaves: RawLeaf[] = []
  collectLeaves(filters, rawLeaves)

  const enabledRaw = rawLeaves.filter(isEnabled)
  const normalized = enabledRaw.map(normalizeLeaf).filter((l): l is NormalizedLeaf => l !== null)

  const metaOf = (l: NormalizedLeaf) =>
    provenance?.[leafSignature(l.column, l.operator, l.rawValue)]

  // Only MANUAL date filters fold into the compact date-range text. Dates added
  // interactively (calendar drill, ask bar, similar-days) carry provenance and
  // render as styled chips below instead — a bare range string would hide where
  // the narrowing came from.
  const dateLeaves = normalized.filter((l) => l.column === 'day0_date' && !metaOf(l))
  const dateRange = formatDateRange(dateLeaves)

  // Group-aware chip collection (preserves OR structure)
  const chipGroups = collectChipGroups(filters)
  const chips: FilterChip[] = []

  for (const group of chipGroups) {
    if (group.type === 'leaf') {
      const leaf = group.leaves[0]
      if (!leaf) continue
      const meta = metaOf(leaf)
      // Un-provenanced dates live in the date-range text, not the chip row.
      if (leaf.column === 'day0_date' && !meta) continue
      const chip = formatLeafChip(leaf, columnMap, normalized.length)
      if (leaf.column === 'day0_date') chip.isDate = true
      if (meta) {
        chip.provenance = meta
        // Derived alias for the header's existing Sankey chip styling.
        if (meta.source === 'sankey') chip.fromSankey = true
      }
      chips.push(chip)
    } else {
      // OR group — combine children into one chip
      const nonDateLeaves = group.leaves.filter((l) => l.column !== 'day0_date')
      if (nonDateLeaves.length === 0) continue
      const parts = nonDateLeaves.map((leaf) => formatLeafChip(leaf, columnMap, 1).display)
      const combined = parts.join(' or ')
      chips.push({
        column: nonDateLeaves[0].column,
        label: '',
        display: combined,
        isDate: false,
        isOrGroup: true,
      })
    }
  }

  return {
    dateRange,
    chips,
    totalCount: normalized.length,
  }
}

function formatLeafChip(
  leaf: NormalizedLeaf,
  columnMap: Record<string, ColumnCatalogDef>,
  totalNonDate: number,
): FilterChip {
  const def = columnMap[leaf.column]
  const label = def?.label ?? leaf.column
  const dataType = def?.dataType ?? 'number'
  const { operator: op, value: val } = leaf

  // Shape-similarity leaf: the value is a JSON exemplar, not a comparable scalar. Show a readable
  // summary instead of the raw JSON blob. (Kept generic here so shared surfaces like the profiles
  // preview render it too; the filter builder renders its own rich PatternFilterRow.)
  if (leaf.column === 'pattern_match') {
    let display = label
    try {
      const c = typeof val === 'string' ? JSON.parse(val) : val
      if (c && typeof c === 'object' && 'ticker' in c && 'tradingDay' in c) {
        display = `Similar to ${(c as { ticker: string }).ticker} ${(c as { tradingDay: string }).tradingDay}`
      }
    } catch {
      /* keep the plain label */
    }
    return { column: leaf.column, label, display, isDate: false }
  }

  // A drilled date (a day, or a week/month span) reads as a date, not as
  // "Date BTW 2026-05-11–2026-05-17".
  if (leaf.column === 'day0_date') {
    const display = formatDateRange([leaf])
    if (display) return { column: leaf.column, label, display, isDate: true }
  }

  // Enum values display via their option LABELS (e.g. tag names, not ids;
  // "Common Stock", not 'CS'). Unknown values (e.g. a deleted tag's id in a
  // saved filter) fall back to the raw value so stale refs stay visible.
  const enumLabel = (v: unknown): unknown =>
    def?.enumOptions?.find((o) => o.value === String(v))?.label ?? v

  const dateDisplay = leaf.isColumnCompare ? null : formatDateValue(op, val)

  let display: string
  if (dateDisplay) {
    display = `${label}: ${dateDisplay}`
  } else if (leaf.isColumnCompare && typeof val === 'string') {
    const rightDef = columnMap[val]
    display = `${label} ${operatorSymbol(op)} ${rightDef?.label ?? val}`
  } else if (op === 'in' || op === 'not_in') {
    const labelled = def?.enumOptions && Array.isArray(val) ? val.map(enumLabel) : enumLabel(val)
    const prefix = op === 'not_in' ? 'not ' : ''
    display = `${prefix}${formatValue(labelled, op, dataType)}`
    if (totalNonDate > 1) {
      display = `${label}: ${display}`
    }
  } else if (op === 'is_true' || op === 'is_false') {
    display = `${label}: ${formatValue(val, op, dataType)}`
  } else {
    display = `${label} ${operatorSymbol(op)} ${formatValue(val, op, dataType)}`
  }

  return { column: leaf.column, label, display, isDate: false }
}

// ─── Tree Walking ───

function collectLeaves(node: unknown, out: RawLeaf[]): void {
  if (!node) return

  if (Array.isArray(node)) {
    for (const child of node) {
      collectLeaves(child, out)
    }
    return
  }

  if (typeof node !== 'object') return

  // Group node: { AND: [...] } or { OR: [...] }
  if (isGroup(node)) {
    const rec = node as Record<string, unknown[]>
    if (Array.isArray(rec.AND)) {
      for (const child of rec.AND) collectLeaves(child, out)
    }
    if (Array.isArray(rec.OR)) {
      for (const child of rec.OR) collectLeaves(child, out)
    }
    return
  }

  // Leaf node — accept if it has column (form) or left.column (query)
  const leaf = node as RawLeaf
  if (leaf.column || leaf.left?.column) {
    out.push(leaf)
  }
}

// ─── Group-Aware Chip Collection ───

interface ChipGroup {
  type: 'leaf' | 'or'
  leaves: NormalizedLeaf[]
  /** The tree nodes it came from (a leaf, or the OR group), by reference. */
  nodes: unknown[]
}

/**
 * Walks the top-level AND group and preserves OR sub-groups so they can be
 * rendered as a single chip with " or " between conditions.
 */
function collectChipGroups(node: unknown): ChipGroup[] {
  if (!node) return []

  // Unwrap arrays (form format wraps in an array)
  if (Array.isArray(node)) {
    const groups: ChipGroup[] = []
    for (const child of node) {
      groups.push(...collectChipGroups(child))
    }
    return groups
  }

  if (typeof node !== 'object') return []

  if (isGroup(node)) {
    const rec = node as Record<string, unknown[]>

    // AND group — walk each child, preserving OR sub-groups
    if (Array.isArray(rec.AND)) {
      const groups: ChipGroup[] = []
      for (const child of rec.AND) {
        if (isGroup(child) && 'OR' in (child as Record<string, unknown>)) {
          // OR sub-group — collect all leaves inside as one group
          const orChildren = (child as Record<string, unknown[]>).OR
          if (Array.isArray(orChildren)) {
            const leaves: NormalizedLeaf[] = []
            for (const orChild of orChildren) {
              const raw = orChild as RawLeaf
              if (isEnabled(raw)) {
                const n = normalizeLeaf(raw)
                if (n) leaves.push(n)
              }
            }
            if (leaves.length > 0) {
              groups.push({ type: 'or', leaves, nodes: [child] })
            }
          }
        } else if (isGroup(child)) {
          // Nested AND — recurse
          groups.push(...collectChipGroups(child))
        } else {
          // Leaf
          const raw = child as RawLeaf
          if (isEnabled(raw)) {
            const n = normalizeLeaf(raw)
            if (n) groups.push({ type: 'leaf', leaves: [n], nodes: [child] })
          }
        }
      }
      return groups
    }

    // Top-level OR (unlikely at root but handle gracefully)
    if (Array.isArray(rec.OR)) {
      const leaves: NormalizedLeaf[] = []
      for (const child of rec.OR) {
        const raw = child as RawLeaf
        if (isEnabled(raw)) {
          const n = normalizeLeaf(raw)
          if (n) leaves.push(n)
        }
      }
      if (leaves.length > 0) return [{ type: 'or', leaves, nodes: [node] }]
    }
  }

  // Single leaf at root
  const raw = node as RawLeaf
  if (raw.column || raw.left?.column) {
    if (isEnabled(raw)) {
      const n = normalizeLeaf(raw)
      if (n) return [{ type: 'leaf', leaves: [n], nodes: [node] }]
    }
  }

  return []
}

// ─── Condition rows (one row per condition, removable) ───

/** One condition as a row: an OR group reads as one; the date window as one. */
export interface FilterConditionRow {
  display: string
  isOrGroup: boolean
  isDate: boolean
}

interface RowWithNodes extends FilterConditionRow {
  nodes: unknown[]
}

/**
 * The rows and the tree nodes behind each, in tree order: the date window
 * first (every enabled date condition, read as one range), then each
 * condition or OR group. Read with the same walk as the chips, so a row's
 * text matches its chip's.
 */
function conditionRowsWithNodes(
  filters: unknown,
  columnMap: Record<string, ColumnCatalogDef>,
): RowWithNodes[] {
  const rawLeaves: RawLeaf[] = []
  collectLeaves(filters, rawLeaves)
  const enabled = rawLeaves.filter(isEnabled)
  const normalized = enabled.map(normalizeLeaf).filter((l): l is NormalizedLeaf => l !== null)
  const rows: RowWithNodes[] = []
  const dateNodes = enabled.filter((raw) => normalizeLeaf(raw)?.column === 'day0_date')
  const dateRange = formatDateRange(normalized.filter((l) => l.column === 'day0_date'))
  if (dateRange && dateNodes.length > 0)
    rows.push({ display: dateRange, isOrGroup: false, isDate: true, nodes: dateNodes })
  for (const group of collectChipGroups(filters)) {
    const leaves = group.leaves.filter((l) => l.column !== 'day0_date')
    if (leaves.length === 0) continue
    rows.push(
      group.type === 'or'
        ? {
            display: leaves.map((l) => formatLeafChip(l, columnMap, 1).display).join(' or '),
            isOrGroup: true,
            isDate: false,
            nodes: group.nodes,
          }
        : {
            display: formatLeafChip(leaves[0], columnMap, normalized.length).display,
            isOrGroup: false,
            isDate: false,
            nodes: group.nodes,
          },
    )
  }
  return rows
}

/** A filter tree's conditions, one row each (the Universe node's Conditions list). */
export function filterConditionRows(
  filters: unknown,
  columnMap: Record<string, ColumnCatalogDef>,
): FilterConditionRow[] {
  return conditionRowsWithNodes(filters, columnMap).map(({ nodes: _nodes, ...row }) => row)
}

/**
 * The tree without row `index`'s conditions (all of the date window's, or a
 * whole OR group), with emptied groups pruned; null when nothing is left, and
 * the tree unchanged for a row that does not exist.
 */
export function withoutConditionRow<T>(
  filters: T,
  index: number,
  columnMap: Record<string, ColumnCatalogDef>,
): T | null {
  const row = conditionRowsWithNodes(filters, columnMap)[index]
  if (!row) return filters
  const drop = new Set(row.nodes)
  const prune = (node: unknown): unknown => {
    if (drop.has(node)) return undefined
    if (Array.isArray(node)) {
      const kept = node.map(prune).filter((child) => child !== undefined)
      return kept.length > 0 ? kept : undefined
    }
    if (isGroup(node)) {
      const rec = node as Record<string, unknown>
      const key = Array.isArray(rec.AND) ? 'AND' : Array.isArray(rec.OR) ? 'OR' : null
      if (!key) return node
      const kept = (rec[key] as unknown[]).map(prune).filter((child) => child !== undefined)
      return kept.length > 0 ? { ...rec, [key]: kept } : undefined
    }
    return node
  }
  const next = prune(filters)
  return next === undefined ? null : (next as T)
}
